/**
 * ZUNO — notificationService.js
 * ─────────────────────────────────────────────────────────────
 * Gerencia permissões, tokens FCM e envio de notificações push
 * Funciona tanto no navegador (web push) quanto no celular
 * ─────────────────────────────────────────────────────────────
 */

import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { auth, db } from "../firebase";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";

// ✅ Sua chave VAPID do Firebase Console
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || "";

let messaging = null;
let nativeToken = null;
let nativeUserType = "passenger";
let nativePlatform = "unknown";
let nativeListenersPromise = null;
const nativeForegroundHandlers = new Set();

async function salvarToken(token, userType, plataforma) {
  const user = auth.currentUser;
  if (!user || !token) return;
  await httpsCallable(functions, "registerPushToken")({ token, userType, platform: plataforma });
}

async function garantirListenersNativos() {
  if (nativeListenersPromise) return nativeListenersPromise;
  nativeListenersPromise = Promise.all([
    FirebaseMessaging.addListener("tokenReceived", async event => {
      nativeToken = event.token;
      try {
        await salvarToken(nativeToken, nativeUserType, nativePlatform);
      } catch (error) {
        console.error("Erro ao salvar token FCM nativo:", { code: error?.code || "push/token-save" });
      }
    }),
    FirebaseMessaging.addListener("notificationReceived", event => {
      const notification = event.notification || {};
      const payload = {
        title: notification.title,
        body: notification.body,
        data: notification.data || {},
      };
      nativeForegroundHandlers.forEach(handler => handler(payload));
    }),
    FirebaseMessaging.addListener("notificationActionPerformed", action => {
      window.dispatchEvent(new CustomEvent("zuno:push-action", {
        detail: action.notification?.data || {},
      }));
    }),
  ]);
  return nativeListenersPromise;
}

export async function registrarListenersNotificacoesNativas() {
  if (!Capacitor.isNativePlatform()) return;
  await garantirListenersNativos();
}

async function inicializarNotificacoesNativas(userType) {
  nativeUserType = userType;
  nativePlatform = Capacitor.getPlatform();
  await garantirListenersNativos();

  let permissao = await FirebaseMessaging.checkPermissions();
  if (permissao.receive === "prompt" || permissao.receive === "prompt-with-rationale") {
    permissao = await FirebaseMessaging.requestPermissions();
  }
  if (permissao.receive !== "granted") {
    console.warn("Permissão de notificação nativa negada.");
    return null;
  }

  if (nativePlatform === "android") {
    await FirebaseMessaging.createChannel({
      id: "zuno_corridas",
      name: "Corridas Zuno",
      description: "Solicitações e atualizações de corridas",
      importance: 5,
      visibility: 1,
      vibration: true,
    }).catch(error => console.warn("Canal de push Android não criado:", error));
  }

  const tokenResult = await FirebaseMessaging.getToken();
  nativeToken = tokenResult.token || null;
  if (!nativeToken) return null;
  await salvarToken(nativeToken, userType, nativePlatform);
  return nativeToken;
}

function getMessagingInstance() {
  if (!messaging) {
    try {
      messaging = getMessaging();
    } catch (e) {
      console.warn("FCM não disponível:", e.message);
      return null;
    }
  }
  return messaging;
}

// ─── PEDIR PERMISSÃO E SALVAR TOKEN ──────────────────────────
export async function inicializarNotificacoes(userType) {
  try {
    if (Capacitor.isNativePlatform()) {
      return await inicializarNotificacoesNativas(userType);
    }

    // Verifica suporte
    if (!("Notification" in window)) {
      console.warn("Notificações não suportadas neste navegador.");
      return null;
    }

    const msg = getMessagingInstance();
    if (!msg) return null;

    // Se já negou antes, não pergunta de novo (evita popup toda vez)
    if (Notification.permission === "denied") return null;

    // Se já concedeu, não pede de novo
    if (Notification.permission !== "granted") {
      const permissao = await Notification.requestPermission();
      if (permissao !== "granted") {
        console.warn("Permissão de notificação negada.");
        return null;
      }
    }

    // Obtém token FCM
    const token = await getToken(msg, { vapidKey: VAPID_KEY });
    if (!token) {
      console.warn("Não foi possível obter token FCM.");
      return null;
    }

    await salvarToken(token, userType, "web");
    console.log("✅ Token FCM salvo no Firestore");

    return token;
  } catch (err) {
    console.error("Erro ao inicializar notificações:", err);
    return null;
  }
}

// ─── ESCUTAR NOTIFICAÇÕES COM APP ABERTO (FOREGROUND) ────────
export function escutarNotificacoesForeground(onNotificacao) {
  if (Capacitor.isNativePlatform()) {
    if (onNotificacao) nativeForegroundHandlers.add(onNotificacao);
    garantirListenersNativos().catch(error => console.error("Erro nos listeners push nativos:", { code: error?.code || "push/listeners" }));
    return () => nativeForegroundHandlers.delete(onNotificacao);
  }

  const msg = getMessagingInstance();
  if (!msg) return () => {};

  return onMessage(msg, (payload) => {
    const { title, body } = payload.notification || {};
    const data = payload.data || {};

    // Mostra notificação nativa do browser se app está em foco
    if (Notification.permission === "granted") {
      new Notification(title || "Zuno", {
        body: body || "",
        icon: "/logo192.png",
        tag: data.tipo || "zuno",
        data,
      });
    }

    // Chama callback para atualizar UI se necessário
    if (onNotificacao) onNotificacao({ title, body, data });
  });
}

// ─── ENVIAR NOTIFICAÇÃO VIA FIRESTORE (TRIGGER BACKEND) ──────
// Como não temos servidor Node.js, usamos uma coleção "notifications"
// no Firestore que pode ser processada por Cloud Functions ou
// pelo próprio app via HTTP direto à API FCM v1
export async function enviarNotificacao({
  destinatarioId,
  destinatarioTipo, // "driver" | "passenger"
  tipo,
  titulo,
  corpo,
  dados = {},
}) {
  try {
    // Busca token FCM do destinatário
    const colecao = destinatarioTipo === "driver" ? "drivers" : "users";
    const snap = await getDoc(doc(db, colecao, destinatarioId));
    if (!snap.exists()) return;

    const token = snap.data().fcmToken;
    if (!token) {
      console.warn("Destinatário não tem token FCM.");
      return;
    }

    // Salva na coleção "notifications" para processamento
    // (Cloud Function pode escutar essa coleção e disparar o FCM)
    await addDoc(collection(db, "notifications"), {
      to: token,
      destinatarioId,
      destinatarioTipo,
      tipo,
      notification: { title: titulo, body: corpo },
      data: { tipo, ...dados },
      enviado: false,
      criadoEm: serverTimestamp(),
    });

    console.log("✅ Notificação enfileirada:", tipo);
  } catch (err) {
    console.error("Erro ao enfileirar notificação:", err);
  }
}

// ─── NOTIFICAÇÕES ESPECÍFICAS DO ZUNO ────────────────────────

// Passageiro: motorista aceitou a corrida
export async function notificarCorridaAceita({ passageiroId, motoristaNome, rideId }) {
  await enviarNotificacao({
    destinatarioId: passageiroId,
    destinatarioTipo: "passenger",
    tipo: "motorista_aceito",
    titulo: "Motorista encontrado! 🚗",
    corpo: `${motoristaNome} aceitou sua corrida e está a caminho.`,
    dados: { rideId, motoristaNome },
  });
}

// Passageiro: motorista está a caminho
export async function notificarMotoristaCaminho({ passageiroId, motoristaNome, rideId }) {
  await enviarNotificacao({
    destinatarioId: passageiroId,
    destinatarioTipo: "passenger",
    tipo: "motorista_caminho",
    titulo: "Motorista a caminho! 📍",
    corpo: `${motoristaNome} está se aproximando da sua localização.`,
    dados: { rideId, motoristaNome },
  });
}

// Passageiro: corrida concluída
export async function notificarCorridaConcluida({ passageiroId, valor, rideId }) {
  await enviarNotificacao({
    destinatarioId: passageiroId,
    destinatarioTipo: "passenger",
    tipo: "corrida_concluida",
    titulo: "Corrida concluída! ✅",
    corpo: `Sua corrida foi concluída. Valor: R$ ${Number(valor).toFixed(2)}`,
    dados: { rideId, valor: String(valor) },
  });
}

// Motorista: nova corrida disponível
export async function notificarNovaCorrida({ motoristaId, passageiroNome, valor, rideId }) {
  await enviarNotificacao({
    destinatarioId: motoristaId,
    destinatarioTipo: "driver",
    tipo: "nova_corrida",
    titulo: "Nova corrida disponível! 🔔",
    corpo: `${passageiroNome} • R$ ${Number(valor).toFixed(2)}`,
    dados: { rideId, passageiroNome, valor: String(valor) },
  });
}

// Motorista: corrida cancelada pelo passageiro
export async function notificarCorridaCancelada({ motoristaId, rideId }) {
  await enviarNotificacao({
    destinatarioId: motoristaId,
    destinatarioTipo: "driver",
    tipo: "corrida_cancelada",
    titulo: "Corrida cancelada ❌",
    corpo: "O passageiro cancelou a corrida.",
    dados: { rideId },
  });
}

export async function limparTokenNotificacoes(userType) {
  const user = auth.currentUser;
  if (!user) return;
  const token = nativeToken || (Capacitor.isNativePlatform()
    ? (await FirebaseMessaging.getToken().catch(() => null))?.token
    : await getToken(getMessagingInstance(), { vapidKey: VAPID_KEY }).catch(() => null));
  if (token) await httpsCallable(functions, "unregisterPushToken")({ token, userType }).catch(() => {});
  if (Capacitor.isNativePlatform()) await FirebaseMessaging.deleteToken().catch(() => {});
  nativeToken = null;
}
